import { traceAuthFlow } from './debug';
import { logger } from '@/lib/utils/logger';

interface AuthMetrics {
  signInAttempts: number;
  signInSuccesses: number;
  signInFailures: number;
  sessionRestoreAttempts: number;
  sessionRestoreSuccesses: number;
  sessionRestoreFailures: number;
  profileFetchAttempts: number;
  profileFetchSuccesses: number;
  profileFetchFailures: number;
  avgSignInTime: number;
  avgSessionRestoreTime: number;
  avgProfileFetchTime: number;
}

class AuthMonitor {
  private metrics: AuthMetrics = {
    signInAttempts: 0,
    signInSuccesses: 0,
    signInFailures: 0,
    sessionRestoreAttempts: 0,
    sessionRestoreSuccesses: 0,
    sessionRestoreFailures: 0,
    profileFetchAttempts: 0,
    profileFetchSuccesses: 0,
    profileFetchFailures: 0,
    avgSignInTime: 0,
    avgSessionRestoreTime: 0,
    avgProfileFetchTime: 0
  };

  private timers = new Map<string, number>();

  startTimer(operation: string) {
    this.timers.set(operation, Date.now());
    traceAuthFlow(`${operation}_start`);
  }

  endTimer(operation: string, success: boolean, error?: Error) {
    const startTime = this.timers.get(operation);
    if (!startTime) return;

    const duration = Date.now() - startTime;
    this.timers.delete(operation);

    // Update metrics
    switch (operation) {
      case 'signIn':
        this.metrics.signInAttempts++;
        if (success) {
          this.metrics.signInSuccesses++;
        } else {
          this.metrics.signInFailures++;
        }
        this.updateAverage('avgSignInTime', duration);
        break;
      case 'sessionRestore':
        this.metrics.sessionRestoreAttempts++;
        if (success) {
          this.metrics.sessionRestoreSuccesses++;
        } else {
          this.metrics.sessionRestoreFailures++;
        }
        this.updateAverage('avgSessionRestoreTime', duration);
        break;
      case 'profileFetch':
        this.metrics.profileFetchAttempts++;
        if (success) {
          this.metrics.profileFetchSuccesses++;
        } else {
          this.metrics.profileFetchFailures++;
        }
        this.updateAverage('avgProfileFetchTime', duration);
        break;
    }

    traceAuthFlow(`${operation}_end`, {
      success,
      duration,
      error: error?.message
    });

    // Log if operation is slow
    if (duration > 1000) {
      logger.warn(`Slow ${operation}: ${duration}ms`);
    }
  }

  private updateAverage(key: keyof AuthMetrics, newValue: number) {
    const current = this.metrics[key] as number;
    const count = this.getCount(key);
    this.metrics[key] = ((current * (count - 1)) + newValue) / count as any;
  }

  private getCount(avgKey: string): number {
    if (avgKey.includes('SignIn')) return this.metrics.signInAttempts;
    if (avgKey.includes('SessionRestore')) return this.metrics.sessionRestoreAttempts;
    if (avgKey.includes('ProfileFetch')) return this.metrics.profileFetchAttempts;
    return 1;
  }

  getMetrics(): AuthMetrics {
    return { ...this.metrics };
  }

  logMetrics() {
    // Keep console methods for debug table display
    console.group('📊 Auth Metrics');
    console.table(this.metrics);
    console.groupEnd();
  }

  reset() {
    this.metrics = {
      signInAttempts: 0,
      signInSuccesses: 0,
      signInFailures: 0,
      sessionRestoreAttempts: 0,
      sessionRestoreSuccesses: 0,
      sessionRestoreFailures: 0,
      profileFetchAttempts: 0,
      profileFetchSuccesses: 0,
      profileFetchFailures: 0,
      avgSignInTime: 0,
      avgSessionRestoreTime: 0,
      avgProfileFetchTime: 0
    };
    this.timers.clear();
  }
}

export const authMonitor = new AuthMonitor();

// Export for window access during debugging
if (typeof window !== 'undefined') {
  (window as any).authMonitor = authMonitor;
} 